import {IDateISO} from 'coreUtils/IUtils';

export interface ICustomerLoginCredentials {
	type?: ECustomerAuthType;
	email: string;
	password: string;
	customerData?: ICustomerRegistrationData;
}

export interface ICustomerBirthday extends IDateISO {}

export enum ECustomerGender {
	SELECT_OPTION = undefined,
	NO_PREFERENCE = 0,
	MALE = 1,
	FEMALE = 2,
	OTHER = 3,
	PREFER_NOT_TO_SAY = 4,
}

export enum ECustomerAuthType {
	REGISTERED = 'registered',
	GUEST = 'guest',
	AGENT = 'agent'
}

export interface ICustomerProfileData {
	firstName?: string;
	lastName?: string;
	email?: string;
	password?: string;
	currentPassword?: string;
	gender?: ECustomerGender;
	salutation?: string;
	birthday?: ICustomerBirthday;
	phone?: string;
	phoneCode?: string;
	city?: string;
	country?: string;
	citizenship?: string;
	privacyAuthorization?: boolean;
	newsletterSubscriptions?: string[];
	contactEmail?: boolean;
	contactSms?: boolean;
	contactWhatsapp?: boolean;
	contactPhone?: boolean;
}

export interface ICustomerRegistrationData extends ICustomerProfileData {
	joinToLoyalty?: boolean;
	authorizationDataCollection?: boolean;
	acceptsMarketing?: boolean;
	privacyAuthorization?: boolean;
	source?: string;
	newsletterSource?: string;
	newsFor?: string;
	c_visa_gold_chameleon?: boolean;
	c_authorizationOrderToken?: string;
	c_orderNo?: string;
	newsletterSubscriptions?: string[];
	/** Explicit US-specific marketing consent flag, distinct from `authorizationDataCollection`/`acceptsMarketing`. */
	USConsent?: boolean;
}

export interface ICustomerQualifiers {
	customQualifiers: ICustomerCustomQualifiers;
	assignmentQualifiers?: ICustomerCustomQualifiers;
	sourceCode?: string;
}

export interface ICustomerCustomQualifiers {
	customergroupId: string;
	deviceType?: string;
	platform: string;
	shippingCountry: string;
}

export interface ICustomerUpdatePasswordData {
	password: string;
	currentPassword: string;
}

export enum EAddressTypes {
	HOME = 'HOME',
	SELL_TO = 'SELL_TO',
	SHIP_TO = 'SHIP_TO'
}

export interface ICustomerAddress {
	addressId?: string;
	preferred?: boolean;
	firstName: string;
	lastName: string;
	fullName?: string;
	address1: string;
	address2?: string;
	city: string;
	countryCode: string;
	stateCode: string;
	c_phoneCode?: string;
	phone: string;
	postalCode: string;
	lastModified?: string;
	creationDate?: string;
	c_persCustClearenceCode?: string;
	addressName?: string;
	salutation?: string;
	c_externalId?: string;
	c_type?: EAddressTypes[keyof EAddressTypes];
}

export interface ICustomerPaymentInstrument {
	bankRoutingNumber?: string;
	creationDate?: string;
	lastModified?: string;
	maskedGiftCertificateCode?: string;
	paymentBankAccount: IPaymentBankAccount;
	paymentCard: IPaymentCard;
	paymentInstrumentId: string;
	paymentMethodId: string;
}

export interface IPaymentBankAccount {
	driversLicenseLastDigits?: string;
	driversLicenseStateCode?: string;
	holder?: string;
	maskedDriversLicense?: string;
	maskedNumber?: string;
	numberLastDigits?: string;
}

export interface IPaymentCard {
	cardType: string;
	creditCardExpired?: boolean;
	creditCardToken?: string;
	expirationMonth?: number;
	expirationYear?: number;
	holder?: string;
	issueNumber?: string;
	maskedNumber?: string;
	numberLastDigits?: string;
	validFromMonth?: number;
	validFromYear?: number;
}

export interface ICustomerAPI {
	c_privacyAuthorization: boolean;
	authType: string;
	customerId: string;
	customerNo: string;
	addresses: Array<ICustomerAddress>;
	login: string;
	email: string;
	firstName: string;
	lastName: string;
	birthday: string;
	gender: ECustomerGender;
	salutation?: string;
	phoneHome: string;
	enabled: boolean;
	isError?: boolean;
	detail?: string;
	paymentInstruments: Array<ICustomerPaymentInstrument>;
	c_phoneCode: string;
	c_storeCredits: string;
	c_country: string;
	c_newsletter: boolean;
	c_authorizationDataCollection: boolean;
	c_crmId: string;
	c_canLoginOnBehalf: boolean;
	c_newsletterSubscriptions: string[];
	c_contactEmail: boolean;
	c_contactSms: boolean;
	c_contactWhatsapp: boolean;
	c_contactPhone: boolean;
	c_city: string;
	c_citizenship: string;
	c_USConsent?: boolean;
	needsCommerceContext: boolean;
}

export interface ICustomerProfilePayload {
	c_authorizationDataCollection: boolean;
	firstName: string;
	lastName: string;
	c_phoneCode?: string;
	c_citizenship?: string;
	c_city?: string;
	phoneHome?: string;
	gender?: ECustomerGender;
	salutation?: string;
	c_newsFor?: string;
	birthday?: string;
	c_languageCode: string;
	c_countryCode: string;
	c_newsletter?: boolean;
	c_joinToLoyalty?: boolean;
	email: string;
	login?: string;
	c_password?: string;
	c_currentPassword?: string;
	c_visa_gold_chameleon: boolean;
	c_authorizationOrderToken?: string;
	c_orderNo?: string;
	c_crmId: string;
	c_source?: string;
	c_device?: string;
	c_subscriptionByChannel?: string;
	c_privacyAuthorization?: string;
	c_newsletterSubscriptions?: string[];
	c_contactEmail?: boolean;
	c_contactSms?: boolean;
	c_contactWhatsapp?: boolean;
	c_contactPhone?: boolean;
	c_USConsent?: boolean;
}

export interface ICustomerRegistrationPayload {
	customer: ICustomerProfilePayload;
	password: string;
}

export interface INewsletterProfile extends Partial<INewsletterPreferencesRequired> {
	email: string;
}

export interface INewsletterPreferences extends Partial<INewsletterPreferencesRequired> {}

interface INewsletterPreferencesRequired {
	acceptsMarketing: boolean;
	privacyAuthorization: boolean;
	authorizationDataCollection: boolean;
	gender: number;
	salutation: string;
	firstName: string;
	lastName: string;
	countryCode: string;
	languageCode: string;
	newsFor: string;
	isSubscribed: boolean;
	customerMonetaryValue: string;
	preferredNewsletterContent: string[];
	preferredCategories: string[];
	preferredBrands: string[];
	brandPreference?: string[];
	subscriptions: string[];
	followedBrands: string[];
	phoneCode?: string;
	phone?: string;
	contactEmail?: boolean;
	contactSms?: boolean;
	contactWhatsapp?: boolean;
	contactPhone?: boolean;
	USConsent?: boolean;
}

export interface INewsletterSource {
	newsletterSource?: string;
	newsletterSourceDetails?: {
		pageType?: string;
	}
}
